import * as THREE from 'three/webgpu';
import type { Viewer } from '../../viewer/Viewer';
import type { MeasureNormalDistanceLineRenderInput } from '../types/measure-callout-types';
import { MEASURE_NORMAL_DISTANCE_LINE_RENDER_ORDER } from '../utils/measureOverlayRenderLayering';
import {
    createMeasureDashedLine,
    writeMeasureDashedLinePositions,
    type MeasureScreenSpaceDashedLine,
} from '../utils/createMeasureDashedLine';

/** 垂直距离线颜色（紫色，与黑色最短距离线、黄色辅助线区分）。 */
const MEASURE_NORMAL_DISTANCE_LINE_COLOR = 0x9933ff;

/** 正交辅助线颜色（黄色虚线）。 */
const MEASURE_NORMAL_DISTANCE_TRANSVERSE_LINE_COLOR = 0xffff00;

/** 退化线段判定容差（世界单位）。 */
const MEASURE_LINE_LENGTH_EPSILON = 1e-9;

/**
 * 单条测量线槽位（复用几何与材质，避免高频重建）。
 */
interface MeasureLineSlot {
    /** 线段对象；未创建时为 null。 */
    line: MeasureScreenSpaceDashedLine | null;
    /** 线段颜色。 */
    color: number;
}

/**
 * 垂直距离测量线三维渲染器。
 *
 * 职责：在点+直线/平面且垂直距离与全局最短距离不等时，按需绘制：
 * 1. 垂直距离线（点侧 → 无限几何垂足，紫色屏空间虚线）；
 * 2. 正交辅助线（有限体最近点 → 垂足 → 同向延伸段，黄色屏空间虚线）。
 */
export class MeasureNormalDistanceLineRenderer {
    /** Viewer 实例。 */
    private readonly viewer: Viewer;

    /** 测量线 overlay 根节点。 */
    private overlayRoot: THREE.Group | null = null;

    /** 垂直距离线槽位。 */
    private readonly normalSlot: MeasureLineSlot = {
        line: null,
        color: MEASURE_NORMAL_DISTANCE_LINE_COLOR,
    };

    /** 正交辅助线槽位。 */
    private readonly transverseSlot: MeasureLineSlot = {
        line: null,
        color: MEASURE_NORMAL_DISTANCE_TRANSVERSE_LINE_COLOR,
    };

    /**
     * @param viewer Viewer 实例。
     */
    public constructor(viewer: Viewer) {
        this.viewer = viewer;
    }

    /**
     * 更新或清空垂直距离线与正交辅助线显示。
     *
     * @param input 渲染输入；为 null 时隐藏全部线段。
     */
    public setNormalDistanceLine(input: MeasureNormalDistanceLineRenderInput | null): void {
        this.ensureOverlayRoot();

        if (!input || !this.overlayRoot) {
            this.hideAllLines();
            this.viewer.requestRender?.();
            return;
        }

        const hasNormalLine = hasSegmentLength(input.from, input.to);
        if (hasNormalLine) {
            this.upsertSegmentLine(this.normalSlot, input.from, input.to);
        } else {
            this.disposeLineSlot(this.normalSlot);
        }

        const transverseFrom = input.transverseFrom;
        const transverseTo = input.transverseTo;
        const hasTransverseLine = transverseFrom
            && transverseTo
            && hasSegmentLength(transverseFrom, transverseTo);
        if (hasTransverseLine) {
            this.upsertSegmentLine(this.transverseSlot, transverseFrom, transverseTo);
        } else {
            this.disposeLineSlot(this.transverseSlot);
        }

        this.overlayRoot.visible = hasNormalLine || Boolean(hasTransverseLine);
        this.viewer.requestRender?.();
    }

    /**
     * 清空测量线并隐藏 overlay。
     */
    public clear(): void {
        this.hideAllLines();
        this.viewer.requestRender?.();
    }

    /**
     * 释放 overlay 与线段资源。
     */
    public dispose(): void {
        this.clear();
        if (this.overlayRoot) {
            if (this.overlayRoot.parent) {
                this.overlayRoot.parent.remove(this.overlayRoot);
            }
            this.overlayRoot = null;
        }
    }

    /**
     * 确保测量线 overlay 根节点已挂载到场景。
     */
    private ensureOverlayRoot(): void {
        if (this.overlayRoot) {
            return;
        }

        const overlayRoot = new THREE.Group();
        overlayRoot.name = 'MeasureNormalDistanceLineOverlayRoot';
        overlayRoot.visible = false;
        overlayRoot.userData.skipSelection = true;
        overlayRoot.userData.skipSelectionSubtree = true;
        this.viewer.addObject(overlayRoot);
        this.overlayRoot = overlayRoot;
    }

    /**
     * 更新或隐藏单条线段。
     *
     * @param slot 线段槽位。
     * @param start 起点。
     * @param end 终点。
     */
    private upsertSegmentLine(
        slot: MeasureLineSlot,
        start: THREE.Vector3,
        end: THREE.Vector3,
    ): void {
        if (!this.overlayRoot) {
            return;
        }

        if (!slot.line) {
            slot.line = createMeasureDashedLine(
                slot.color,
                MEASURE_NORMAL_DISTANCE_LINE_RENDER_ORDER,
                'MeasureNormalDistanceLine',
            );
            this.overlayRoot.add(slot.line);
        }

        writeMeasureDashedLinePositions(slot.line, start, end);
        slot.line.visible = true;
    }

    /**
     * 隐藏全部线段并释放槽位对象。
     */
    private hideAllLines(): void {
        this.disposeLineSlot(this.normalSlot);
        this.disposeLineSlot(this.transverseSlot);
        if (this.overlayRoot) {
            this.overlayRoot.visible = false;
        }
    }

    /**
     * 释放单槽位线段资源。
     *
     * @param slot 线段槽位。
     */
    private disposeLineSlot(slot: MeasureLineSlot): void {
        if (!slot.line) {
            return;
        }

        if (slot.line.parent) {
            slot.line.parent.remove(slot.line);
        }
        slot.line.geometry.dispose();
        slot.line.material.dispose();
        slot.line = null;
    }
}

/**
 * 判断线段是否具有可渲染长度。
 *
 * @param start 起点。
 * @param end 终点。
 * @returns 长度大于容差时返回 true。
 */
function hasSegmentLength(start: THREE.Vector3, end: THREE.Vector3): boolean {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const dz = end.z - start.z;
    return dx * dx + dy * dy + dz * dz > MEASURE_LINE_LENGTH_EPSILON * MEASURE_LINE_LENGTH_EPSILON;
}
